import API_CONFIG from '../config/api';
import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * Get the current user's authentication token
 */
async function getAuthToken(): Promise<string | undefined> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return undefined;
  }
}

/**
 * Generic API fetch wrapper with timeout and error handling
 */
async function apiFetch<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = `${API_CONFIG.baseUrl}${endpoint}`;

  // Check if we're using a placeholder URL (local development)
  if (API_CONFIG.baseUrl.includes('your-api-gateway-url') || API_CONFIG.baseUrl === 'http://localhost:3000') {
    console.error('API not configured. Please set REACT_APP_API_BASE_URL environment variable.');
    throw new Error('API endpoint not configured. Please deploy the API or set REACT_APP_API_BASE_URL.');
  }

  // Get authentication token
  const authToken = await getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required. Please log in.');
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        ...options?.headers,
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      // Handle 401 Unauthorized specifically
      if (response.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }

      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please try again');
      }
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
}

/**
 * Response type for portfolio summary
 */
export interface PortfolioSummaryResponse {
  activeTradeGroups: number;
  totalRealizedProfit: number;
  totalTargetProfit: number;
  totalUnrealizedProfit: number;
  totalCombinedPnl: number;
  totalCostBasis: number;
  pendingBuyOrdersValue: number;
  totalFeesPaid: number;
  totalOpenOrders: number;
  lastActivity: number | null;
  timestamp: number;
}

/**
 * Fetch portfolio summary
 */
export async function getPortfolioSummary(): Promise<PortfolioSummaryResponse> {
  return apiFetch<PortfolioSummaryResponse>('/v1/metrics/portfolio-summary');
}

// =====================================================
// Portfolio Balance API Functions
// =====================================================

/**
 * Portfolio balance snapshot from the balance tracking system
 */
export interface PortfolioBalanceSnapshot {
  snapshotId: string;
  portfolioUuid: string;
  snapshotTime: number; // epoch milliseconds
  totalBalanceValue: string; // BigDecimal as string
  totalBalanceCurrency: string;
  totalCashEquivalentBalanceValue: string; // BigDecimal as string
  totalCashEquivalentBalanceCurrency: string;
  totalCryptoBalanceValue: string; // BigDecimal as string
  totalCryptoBalanceCurrency: string;
}

/**
 * Portfolio allocation breakdown response
 */
export interface PortfolioAllocationResponse {
  allocations: Array<{
    asset: string;
    isCash: boolean;
    balanceFiat: string;
    balanceCrypto: string;
    allocation: string;
    unrealizedPnl: string;
    costBasis: string;
  }>;
  summary: {
    totalCash: string;
    availableCash: string;
    reservedCash: string;
    totalCrypto: string;
    portfolioTotal: string;
    totalUnrealizedPnl: string;
    cashPositions: number;
    cryptoPositions: number;
    totalPositions: number;
    cashPercentage?: string;
    cryptoPercentage?: string;
  };
  snapshotTime: number;
}

/**
 * Fetch portfolio allocation breakdown with unrealized P&L
 */
export async function getPortfolioAllocation(): Promise<PortfolioAllocationResponse> {
  const url = `${API_CONFIG.portfolioBaseUrl}/v1/portfolio/allocation`;

  const authToken = await getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required. Please log in.');
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please try again');
      }
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
}

/**
 * Fetch the latest portfolio balance snapshot
 */
export async function getLatestPortfolioBalance(): Promise<PortfolioBalanceSnapshot> {
  const url = `${API_CONFIG.portfolioBaseUrl}/v1/portfolio/balance/latest`;

  const authToken = await getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required. Please log in.');
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please try again');
      }
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
}

/**
 * Fetch portfolio balance history within a date range
 * @param hours - Number of hours of history to fetch
 * @param granularity - Data granularity: 'raw', '5min', '1hour', '1day', '1month'
 */
export async function getPortfolioBalanceHistory(hours: number, granularity: Granularity = Granularity.RAW): Promise<PortfolioBalanceSnapshot[]> {
  const url = `${API_CONFIG.portfolioBaseUrl}/v1/portfolio/balance/history?hours=${hours}&granularity=${granularity}`;

  const authToken = await getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required. Please log in.');
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data.snapshots || [];
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please try again');
      }
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
}

// =====================================================
// Capital Deployment API Functions
// =====================================================
/**
 * Capital deployment summary for entire portfolio
 */
export interface CapitalDeploymentSummary {
  totalAllocated: number;  // Total capital allocated (includes WATCHING groups)
  totalDeployed: number;   // Capital in active orders
  pendingBuyCapital: number;
  activePositionCapital: number;
  totalRealizedProfit: number;
  totalTargetProfit: number;
  totalCurrentProfit: number;
  totalFeesPaid: number;
  activeTradeGroups: number;
  totalOpenOrders: number;
  totalTrades: number;
  totalPendingBuys: number;
  totalActiveSells: number;
  firstTradeDate: number | null;
  lastActivity: number | null;
  deploymentEfficiencyPct: number;
  avgProfitPerTrade: number;
  portfolioRoi: number;
  timestamp: number;
}
/**
 * Strategy deployment metrics
 */
export interface StrategyDeployment {
  strategyType: string;
  activeGroups: number;
  allocatedCapital: number;  // Total capital assigned to strategy (includes WATCHING groups)
  deployedCapital: number;   // Capital currently in active orders
  realizedProfit: number;
  targetProfit: number;
  unrealizedProfit: number;
  combinedPnl: number;
  avgReturnPercent: number;
  totalFees: number;
  totalTrades: number;
  pendingBuyOrders: number;
  activeSellOrders: number;
  fillRate: number;
  roi: number;
  allocationPercent: number;
}
/**
 * Response for capital deployment by strategy
 */
export interface CapitalByStrategyResponse {
  strategies: StrategyDeployment[];
  totalAllocated: number;  // Total allocated capital across all strategies
  timestamp: number;
}
/**
 * Product deployment metrics
 */
export interface ProductDeployment {
  productId: string;
  activeGroups: number;
  allocatedCapital: number;  // Total capital assigned to product (includes WATCHING groups)
  deployedCapital: number;   // Capital currently in active orders
  realizedProfit: number;
  targetProfit: number;
  unrealizedProfit: number;
  combinedPnl: number;
  avgReturnPercent: number;
  totalFees: number;
  pendingBuyOrders: number;
  activeSellOrders: number;
  totalTrades: number;
  currentPrice: number;
  twoDayMin: number;
  twoDayMax: number;
  fiveDayMin: number;
  fiveDayMax: number;
  roi: number;
  allocationPercent: number;
}
/**
 * Response for capital deployment by product
 */
export interface CapitalByProductResponse {
  products: ProductDeployment[];
  totalAllocated: number;  // Total allocated capital across all products
  timestamp: number;
}
/**
 * Strategy performance for ranking
 */
export interface StrategyPerformance {
  tradeGroupId: string;
  name: string;
  productId: string;
  tradeGroupType: string;
  state: string;
  totalCapturedProfit: number;
  avgCapturedReturn: number;
  deployedCapital: number;
  roi: number;
  totalTradeCount: number;
  avgProfitPerTrade: number;
  pendingBuyOrders: number;
  activeSellOrders: number;
  totalFees: number;
  createdAt: number | null;
  daysActive: number;
  performanceRating: string;
  lastModified: number | null;
}
/**
 * Response for top/bottom performers
 */
export interface PerformersResponse {
  performers: StrategyPerformance[];
  count: number;
  timestamp: number;
}
/**
 * Fetch capital deployment summary
 */
export async function getCapitalDeploymentSummary(): Promise<CapitalDeploymentSummary> {
  return apiFetch<CapitalDeploymentSummary>('/v1/metrics/capital-deployment/summary');
}
/**
 * Fetch capital deployment by strategy type
 */
export async function getCapitalByStrategy(): Promise<CapitalByStrategyResponse> {
  return apiFetch<CapitalByStrategyResponse>('/v1/metrics/capital-deployment/by-strategy');
}
/**
 * Fetch capital deployment by product (trading pair)
 */
export async function getCapitalByProduct(): Promise<CapitalByProductResponse> {
  return apiFetch<CapitalByProductResponse>('/v1/metrics/capital-deployment/by-product');
}
/**
 * Fetch top performing trade groups
 */
export async function getTopPerformers(limit: number = 10, strategyType?: string): Promise<PerformersResponse> {
  const params = new URLSearchParams();
  params.append('limit', limit.toString());
  if (strategyType) {
    params.append('type', strategyType);
  }
  return apiFetch<PerformersResponse>(`/v1/metrics/capital-deployment/top-performers?${params.toString()}`);
}
/**
 * Fetch bottom performing trade groups
 */
export async function getBottomPerformers(limit: number = 10, strategyType?: string): Promise<PerformersResponse> {
  const params = new URLSearchParams();
  params.append('limit', limit.toString());
  if (strategyType) {
    params.append('type', strategyType);
  }
  return apiFetch<PerformersResponse>(`/v1/metrics/capital-deployment/bottom-performers?${params.toString()}`);
}

// =====================================================
// Market Activity API Functions
// =====================================================

/**
 * Asset-level breakdown of buy vs sell activity
 */
export interface AssetFlowBreakdown {
  productId: string;
  buyVolume: number;
  sellVolume: number;
  buyOrderCount: number;
  sellOrderCount: number;
  avgBuyPrice: number | null;
  avgSellPrice: number | null;
  ratio: number;
}

/**
 * Daily market activity summary
 */
export interface MarketActivitySummary {
  date: string;
  totalBuyVolume: number;
  totalSellVolume: number;
  buyOrderCount: number;
  sellOrderCount: number;
  netFlow: number;
  buyToSellRatio: number;
  marketTrend: 'UP' | 'DOWN' | 'BALANCED';
}

/**
 * Daily summary with asset breakdown (from backend)
 */
export interface DailyActivityDetail {
  date: string;
  totalBuyVolume: number;
  totalSellVolume: number;
  buyOrderCount: number;
  sellOrderCount: number;
  netFlow: number;
  buyToSellRatio: number;
  marketTrend: 'UP' | 'DOWN' | 'BALANCED';
  assetBreakdown: AssetFlowBreakdown[];
}

/**
 * Backend response - always returns dailySummaries array
 */
export interface MarketActivityBackendResponse {
  dailySummaries: DailyActivityDetail[];
  days: number;
  timestamp: number;
}

/**
 * Single-day market activity response (transformed from backend)
 */
export interface MarketActivityResponse {
  summary: MarketActivitySummary;
  assetBreakdown: AssetFlowBreakdown[];
  timestamp: number;
}

/**
 * Multi-day market activity response
 */
export interface MultiDayMarketActivityResponse {
  dailySummaries: DailyActivityDetail[];
  days: number;
  timestamp: number;
}

/**
 * Fetch market activity for a specific date
 * @param date - ISO date string (YYYY-MM-DD) - REQUIRED
 */
export async function getMarketActivity(date: string): Promise<MarketActivityResponse> {
  const params = new URLSearchParams();
  params.append('date', date);
  params.append('days', '1');

  const response = await apiFetch<MarketActivityBackendResponse>(`/v1/metrics/market-activity?${params.toString()}`);

  // Transform backend response to single-day format
  if (response.dailySummaries.length === 0) {
    throw new Error('No market activity data available for the specified date');
  }

  const dayData = response.dailySummaries[0];

  return {
    summary: {
      date: dayData.date,
      totalBuyVolume: dayData.totalBuyVolume,
      totalSellVolume: dayData.totalSellVolume,
      buyOrderCount: dayData.buyOrderCount,
      sellOrderCount: dayData.sellOrderCount,
      netFlow: dayData.netFlow,
      buyToSellRatio: dayData.buyToSellRatio,
      marketTrend: dayData.marketTrend,
    },
    assetBreakdown: dayData.assetBreakdown,
    timestamp: response.timestamp,
  };
}

/**
 * Fetch market activity trend for multiple days
 * @param days - Number of days (1-30)
 * @param endDate - End date (ISO string) - REQUIRED
 */
export async function getMarketActivityTrend(days: number = 7, endDate: string): Promise<MultiDayMarketActivityResponse> {
  const params = new URLSearchParams();
  params.append('days', days.toString());
  params.append('date', endDate);

  return apiFetch<MarketActivityBackendResponse>(`/v1/metrics/market-activity?${params.toString()}`);
}

// =====================================================
// Hourly Net Flow API Functions
// =====================================================

/**
 * Hourly net flow data point
 */
export interface HourlyNetFlowData {
  date: string;
  hour: number;
  buyVolume: number;
  sellVolume: number;
  buyOrderCount: number;
  sellOrderCount: number;
  netFlow: number;
}

/**
 * Hourly net flow response
 */
export interface HourlyNetFlowResponse {
  hourlyData: HourlyNetFlowData[];
  days: number;
  productId?: string;
  timestamp: number;
}

/**
 * Fetch hourly net flow data
 * @param days - Number of days (1-30, defaults to 7)
 * @param endDate - End date (ISO string), defaults to today
 * @param productId - Optional product filter
 */
export async function getHourlyNetFlow(
  days: number = 7,
  endDate?: string,
  productId?: string
): Promise<HourlyNetFlowResponse> {
  const params = new URLSearchParams();
  params.append('days', days.toString());
  if (endDate) {
    params.append('endDate', endDate);
  }
  if (productId) {
    params.append('productId', productId);
  }
  return apiFetch<HourlyNetFlowResponse>(`/v1/metrics/hourly-net-flow?${params.toString()}`);
}

export enum Granularity {
    RAW = 'RAW',
    FIVE_MIN = 'FIVE_MIN',
    HOUR = 'HOUR',
    DAY = 'DAY',
    MONTH = 'MONTH'
}

// =====================================================
// Market Overview API Functions
// =====================================================

/**
 * Market overview item with intelligence for a single cryptocurrency pair
 */
export interface MarketOverviewItem {
  productId: string;
  currentPrice: number;
  priceChange24h: number;
  priceChangePercentage24h: number;
  trend: 'UPTREND' | 'DOWNTREND' | 'SIDEWAYS' | 'UNKNOWN';
  trendStrength: number; // 0.0-1.0
  volatilityState: 'LOW' | 'MEDIUM' | 'HIGH' | 'EXTREME' | 'UNKNOWN';
  volatilityPercentile: number; // 0-100
  momentumDirection: 'BULLISH' | 'BEARISH' | 'NEUTRAL';
  momentumStrength: number; // 0.0-1.0
  marketRegime: 'TRENDING' | 'RANGING' | 'VOLATILE' | 'REVERSAL_POTENTIAL' | 'UNKNOWN';
  volume24h: number;
  relativeVolume: number; // Current vs average (1.0 = average)
  pivotStatus: string; // "Above R1", "At PP", "Below S1", etc.
  interpretation: string; // Human-readable market analysis
  interpretationSentiment: 'BULLISH' | 'BEARISH' | 'NEUTRAL' | 'CAUTION'; // Sentiment for color-coding
  lastUpdated: string; // ISO timestamp
  dataQuality: 'FRESH' | 'STALE' | 'INCOMPLETE';
}

/**
 * Market overview metadata
 */
export interface MarketMetadata {
  totalMarkets: number;
  timestamp: string; // ISO timestamp
  granularityMins: number;
}

/**
 * Market overview response
 */
export interface MarketOverviewResponse {
  markets: MarketOverviewItem[];
  metadata: MarketMetadata;
}

/**
 * Fetch market overview data
 */
export async function getMarketOverview(): Promise<MarketOverviewResponse> {
  return apiFetch<MarketOverviewResponse>('/v1/market-overview');
}

// =====================================================
// Volatility Detail API Functions
// =====================================================

/**
 * Volatility history data point
 */
export interface VolatilityHistoryPoint {
  analysis_time: string; // ISO timestamp
  volatility_state: 'LOW' | 'MEDIUM' | 'HIGH' | 'EXTREME' | 'UNKNOWN';
  volatility_percentile: number;
  trend: string;
  momentum_direction: string;
  market_regime: string;
}

/**
 * ATR history data point
 */
export interface ATRHistoryPoint {
  metric_time: string; // ISO timestamp
  atr_value: number;
}

/**
 * Percentile candle metrics (formerly median-only)
 * Now supports variable percentiles (25th, 50th, 75th, 85th, 90th, 95th, 99th)
 */
export interface PercentileCandleMetrics {
  percentile_gain: number; // Absolute dollar amount at selected percentile
  percentile_loss: number; // Absolute dollar amount at selected percentile
  gain_count: number;
  loss_count: number;
  total_count: number;
  gain_frequency_pct: number; // Percentage of candles that are gains
  loss_frequency_pct: number; // Percentage of candles that are losses
  current_price: number; // Current price of the asset
  percentile_used: number; // Which percentile was calculated (25, 50, 75, etc.)
  gain_pct_of_price?: number; // Gain as percentage of current price
  loss_pct_of_price?: number; // Loss as percentage of current price
}

// Keep old interface for backward compatibility with existing code
export interface MedianCandleMetrics {
  median_gain: number;
  median_loss: number;
  gain_count: number;
  loss_count: number;
  total_count: number;
  gain_frequency_pct: number;
  loss_frequency_pct: number;
}

/**
 * Regime transition
 */
export interface RegimeTransition {
  timestamp: string; // ISO timestamp
  from_regime: string;
  to_regime: string;
}

/**
 * Current volatility metrics
 */
export interface CurrentVolatilityMetrics {
  volatility_state: string;
  volatility_percentile: number;
  trend: string;
  momentum_direction: string;
  market_regime: string;
  analysis_time: string; // ISO timestamp
}

/**
 * Volatility detail response
 */
export interface VolatilityDetailResponse {
  product_id: string;
  start_date: string; // YYYY-MM-DD
  end_date: string; // YYYY-MM-DD
  granularity_mins: number;
  percentile_used: number; // Which percentile was calculated (25, 50, 75, 85, 90, 95, 99)
  current_metrics: CurrentVolatilityMetrics;
  volatility_history: VolatilityHistoryPoint[];
  atr_history: ATRHistoryPoint[];
  percentile_metrics: PercentileCandleMetrics; // New field name
  median_metrics?: MedianCandleMetrics; // Old field name for backward compatibility
  regime_transitions: RegimeTransition[];
  data_points: number;
  generated_at: string; // ISO timestamp
}

/**
 * Fetch detailed volatility analysis for a specific product
 * @param percentile - Which percentile to calculate (25, 50, 75, 85, 90, 95, 99). Default: 50 (median)
 */
export async function getVolatilityDetail(
  productId: string,
  startDate?: string, // YYYY-MM-DD format
  endDate?: string, // YYYY-MM-DD format
  granularity?: number,
  percentile?: number // 25, 50, 75, 85, 90, 95, 99
): Promise<VolatilityDetailResponse> {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  if (granularity) params.append('granularity', granularity.toString());
  if (percentile) params.append('percentile', percentile.toString());

  const queryString = params.toString();
  const url = `/v1/market-overview/${productId}/volatility-detail${queryString ? `?${queryString}` : ''}`;

  return apiFetch<VolatilityDetailResponse>(url);
}

/**
 * Pivot points response interface
 */
export interface PivotPointsResponse {
  productId: string;
  timeframe: 'DAILY' | 'WEEKLY' | 'MONTHLY' | '4H' | '6H' | '12H';
  type: 'STANDARD' | 'FIBONACCI' | 'WOODIE';
  periodEndTime: string; // ISO timestamp
  currentPrice: number;
  prevHigh: number;
  prevLow: number;
  prevClose: number;
  pivotPoint: number;
  resistance1: number | null;
  resistance2: number | null;
  resistance3: number | null;
  support1: number | null;
  support2: number | null;
  support3: number | null;
  status: string; // "Above Pivot Point", "Below Pivot Point", "At Pivot Point"
  nearestSupport: number | null;
  nearestResistance: number | null;
}

/**
 * Fetch pivot points for a specific product
 */
export async function getPivotPoints(
  productId: string,
  timeframe: 'TWENTY_FOUR_HOUR' | 'SEVEN_DAY' | 'THIRTY_DAY' | 'THIRTY_MIN' | 'ONE_HOUR' | 'TWO_HOUR' | 'FOUR_HOUR' | 'EIGHT_HOUR' | 'TWELVE_HOUR' = 'TWENTY_FOUR_HOUR',
  type: 'STANDARD' | 'FIBONACCI' | 'WOODIE' = 'STANDARD'
): Promise<PivotPointsResponse> {
  const params = new URLSearchParams({
    timeframe,
    type,
  });
  return apiFetch<PivotPointsResponse>(`/v1/pivot-points/${productId}?${params}`);
}

// =====================================================
// Candle Data API Functions
// =====================================================

/**
 * Single OHLCV candlestick data point
 */
export interface CandleData {
  timestamp: string; // ISO-8601 timestamp
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  isComplete: boolean;
}

/**
 * Candle data response with metadata
 */
export interface CandleResponse {
  productId: string;
  granularityMins: number;
  startTime: string; // ISO-8601 timestamp
  endTime: string; // ISO-8601 timestamp
  candles: CandleData[];
  candleCount: number;
  hasGaps: boolean;
  timestamp: string; // ISO-8601 timestamp
}

/**
 * Fetch candle data for a specific product and timeframe
 * @param productId - Product identifier (e.g., BTC-USDC, ETH-USDC)
 * @param granularityMins - Candle granularity in minutes (1, 5, 15, 60, 240, 1440, 10080)
 * @param startTime - Start time (ISO-8601) - REQUIRED
 * @param endTime - End time (ISO-8601) - REQUIRED
 */
export async function getCandleData(
  productId: string,
  granularityMins: number,
  startTime: string,
  endTime: string
): Promise<CandleResponse> {
  const params = new URLSearchParams();
  params.append('granularity', granularityMins.toString());
  params.append('startTime', startTime);
  params.append('endTime', endTime);

  return apiFetch<CandleResponse>(`/v1/candles/${productId}?${params.toString()}`);
}

// =====================================================
// Technical Indicators API Functions
// =====================================================

/**
 * Single technical indicator data point
 */
export interface TechnicalIndicatorData {
  metricType: string; // SMA, EMA, RSI, BB_UPPER, BB_MIDDLE, BB_LOWER
  period: number;     // Period of the indicator (e.g., 20, 50, 200)
  metricTime: string; // ISO-8601 timestamp
  value: number;      // Indicator value
  complete: boolean;  // Whether the indicator is based on complete data (Jackson serializes 'isComplete' as 'complete')
}

/**
 * Technical indicators response with metadata
 */
export interface TechnicalIndicatorResponse {
  productId: string;
  granularityMins: number;
  startTime: string; // ISO-8601 timestamp
  endTime: string; // ISO-8601 timestamp
  indicators: TechnicalIndicatorData[];
  indicatorCount: number;
  timestamp: string; // ISO-8601 timestamp
}

/**
 * Fetch technical indicators for a specific product and timeframe
 * Now requests a SINGLE indicator type and period for better performance and caching
 * @param productId - Product identifier (e.g., BTC-USDC, ETH-USDC)
 * @param granularityMins - Candle granularity in minutes (1, 5, 15, 60, 240, 1440)
 * @param type - Single indicator type (SMA, EMA, RSI, BOLLINGER_UPPER, BOLLINGER_MIDDLE, BOLLINGER_LOWER)
 * @param period - Single period value (e.g., 20, 50, 200)
 * @param startTime - Start time (ISO-8601) - REQUIRED
 * @param endTime - End time (ISO-8601) - REQUIRED
 */
export async function getTechnicalIndicator(
  productId: string,
  granularityMins: number,
  type: string,
  period: number,
  startTime: string,
  endTime: string
): Promise<TechnicalIndicatorResponse> {
  const params = new URLSearchParams();
  params.append('granularity', granularityMins.toString());
  params.append('type', type);
  params.append('period', period.toString());
  params.append('startTime', startTime);
  params.append('endTime', endTime);

  return apiFetch<TechnicalIndicatorResponse>(
    `/v1/indicators/${productId}/technical?${params.toString()}`
  );
}

// =====================================================
// MACD Indicator API Functions
// =====================================================

/**
 * MACD indicator data point with all three components
 */
export interface MacdData {
  metricTime: string; // ISO-8601 timestamp
  macdLine: number;    // MACD Line (Fast EMA - Slow EMA)
  signalLine: number;  // Signal Line (EMA of MACD Line)
  histogram: number;   // Histogram (MACD Line - Signal Line)
  complete: boolean;   // Whether the indicator is based on complete data
}

/**
 * MACD indicators response with metadata
 */
export interface MacdResponse {
  productId: string;
  granularityMins: number;
  fastPeriod: number;    // Typically 12
  slowPeriod: number;    // Typically 26
  signalPeriod: number;  // Typically 9
  startTime: string;     // ISO-8601 timestamp
  endTime: string;       // ISO-8601 timestamp
  macdData: MacdData[];
  dataCount: number;
  timestamp: string;     // ISO-8601 timestamp
}

/**
 * Fetch MACD indicators for a specific product and timeframe
 * @param productId - Product identifier (e.g., BTC-USDC, ETH-USDC)
 * @param granularityMins - Candle granularity in minutes
 * @param fastPeriod - Fast EMA period (default 12)
 * @param slowPeriod - Slow EMA period (default 26)
 * @param signalPeriod - Signal EMA period (default 9)
 * @param startTime - Start time (ISO-8601) - REQUIRED
 * @param endTime - End time (ISO-8601) - REQUIRED
 */
export async function getMacdIndicators(
  productId: string,
  granularityMins: number,
  fastPeriod: number = 12,
  slowPeriod: number = 26,
  signalPeriod: number = 9,
  startTime: string,
  endTime: string
): Promise<MacdResponse> {
  const params = new URLSearchParams();
  params.append('granularity', granularityMins.toString());
  params.append('fastPeriod', fastPeriod.toString());
  params.append('slowPeriod', slowPeriod.toString());
  params.append('signalPeriod', signalPeriod.toString());
  params.append('startTime', startTime);
  params.append('endTime', endTime);

  return apiFetch<MacdResponse>(
    `/v1/indicators/${productId}/macd?${params.toString()}`
  );
}

// =====================================================
// Pivot Levels Time-Series API
// =====================================================

/**
 * Single pivot level data point in time-series
 */
export interface PivotLevelDataPoint {
  timestamp: string; // ISO-8601 timestamp
  pivotPoint: number;
  r1: number;
  r2: number;
  r3: number;
  s1: number;
  s2: number;
  s3: number;
  fibR1?: number;
  fibR2?: number;
  fibR3?: number;
  fibS1?: number;
  fibS2?: number;
  fibS3?: number;
}

/**
 * Pivot levels time-series response
 */
export interface PivotLevelsResponse {
  product: string;
  timeframe: string;
  data: PivotLevelDataPoint[];
}

/**
 * Fetch pivot levels time-series data for visualization
 * @param productId - Product identifier (e.g., BTC-USDC)
 * @param timeframe - Pivot timeframe (DAILY, WEEKLY, MONTHLY, etc.)
 * @param start - Start time (ISO-8601)
 * @param end - End time (ISO-8601)
 */
export async function getPivotLevels(
  productId: string,
  timeframe: string,
  start: string,
  end: string
): Promise<PivotLevelsResponse> {
  const params = new URLSearchParams();
  params.append('product', productId);
  params.append('timeframe', timeframe);
  params.append('start', start);
  params.append('end', end);

  return apiFetch<PivotLevelsResponse>(
    `/v1/pivot-levels?${params.toString()}`
  );
}

// =====================================================
// Pivot Interaction Signals API
// =====================================================

/**
 * Single pivot interaction signal data point
 */
export interface PivotSignalDataPoint {
  timestamp: string; // ISO-8601 timestamp
  type: string; // BULLISH_BOUNCE, BEARISH_REJECTION, BULLISH_BREAK, BEARISH_BREAK, NEUTRAL_TOUCH
  level: string; // PP, R1, R2, R3, S1, S2, S3
  price: number;
  strength: number; // 0.0 to 1.0
  timeframe: string;
  createdAt: string; // ISO-8601 timestamp - when the signal was detected
}

/**
 * Pivot signals response
 */
export interface PivotSignalsResponse {
  product: string;
  signals: PivotSignalDataPoint[];
}

/**
 * Fetch pivot interaction signals for chart annotations
 * @param productId - Product identifier (e.g., BTC-USDC)
 * @param start - Start time (ISO-8601)
 * @param end - End time (ISO-8601)
 * @param minStrength - Minimum signal strength (0.0-1.0, default 0.5)
 */
export async function getPivotSignals(
  productId: string,
  start: string,
  end: string,
  minStrength: number = 0.5
): Promise<PivotSignalsResponse> {
  const params = new URLSearchParams();
  params.append('product', productId);
  params.append('start', start);
  params.append('end', end);
  params.append('minStrength', minStrength.toString());

  return apiFetch<PivotSignalsResponse>(
    `/v1/pivot-signals?${params.toString()}`
  );
}